import React, { useCallback, useRef, useState } from 'react';
import clsx from 'clsx';
import styles from './NotificationPanel.module.scss';
import { NotificationPanelProps } from './NotificationCenter.types';
import { INFINITE_SCROLL_THRESHOLD_PX, TAB_ACTIVE_COLOR, TAB_ICON, TAB_INACTIVE_COLOR, TABS } from './NotificationCenter.constants';
import NotificationItem from './NotificationItem';
import CategoryFilterPopover from './CategoryFilterPopover';
import { useOutsideClick } from '../../utils/constants/hooks';
import DoneAllIcon from '../../assets/Svg/DoneAllIcon';
import CrossShapeIcon from '../../assets/Svg/CrossShapeIcon';

const NotificationPanel: React.FC<NotificationPanelProps> = ({
    notifications,
    unreadCount,
    loading,
    loadingMore,
    hasMore,
    onLoadMore,
    activeTab,
    onTabChange,
    onMarkAllRead,
    onClose,
    selectedCategories,
    onCategoriesChange,
    onItemAction,
    onMarkRead,
    onMarkUnread,
    fullPage = false,
}) => {
    const [filterOpen, setFilterOpen] = useState(false);
    const filterAnchorRef = useRef<HTMLDivElement>(null);

    useOutsideClick(filterAnchorRef, () => setFilterOpen(false));

    const handleMarkAllRead = async (e: React.MouseEvent) => {
        e.stopPropagation();
        await onMarkAllRead();
    };

    const handleScroll = useCallback(
        (e: React.UIEvent<HTMLDivElement>) => {
            if (!hasMore || loadingMore || loading) return;
            const el = e.currentTarget;
            const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
            if (distanceFromBottom <= INFINITE_SCROLL_THRESHOLD_PX) {
                onLoadMore();
            }
        },
        [hasMore, loadingMore, loading, onLoadMore],
    );

    return (
        <div className={clsx(styles.Panel, { [styles.FullPage]: fullPage })} role="dialog" aria-label="Notifications">
            {/* Header */}
            <div className={styles.Header}>
                <h2 className={styles.Title}>Notifications</h2>
                <div className={styles.HeaderActions}>
                    <button type="button" className={styles.MarkAllBtn} onClick={handleMarkAllRead} disabled={unreadCount === 0}>
                        <DoneAllIcon color="currentColor" />
                        Mark all as read
                    </button>
                    <button type="button" className={styles.CloseBtn} onClick={onClose} aria-label="Close notifications">
                        <CrossShapeIcon color="currentColor" />
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className={styles.TabBar}>
                <div className={styles.Tabs}>
                    {TABS.map((tab) => {
                        const Icon = TAB_ICON[tab.id];
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                type="button"
                                className={`${styles.Tab} ${isActive ? styles.ActiveTab : ''}`}
                                onClick={() => onTabChange(tab.id)}
                            >
                                <Icon color={isActive ? TAB_ACTIVE_COLOR : TAB_INACTIVE_COLOR} />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>
                <div className={styles.FilterAnchor} ref={filterAnchorRef}>
                    <button
                        className={styles.FiltersBtn}
                        onClick={(e) => {
                            e.stopPropagation();
                            setFilterOpen((prev) => !prev);
                        }}
                        aria-haspopup="true"
                        aria-expanded={filterOpen}
                        type="button"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M3 6h18M7 12h10M11 18h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                        <span className={styles.FiltersLabel}>Filters</span>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </button>
                    {filterOpen && <CategoryFilterPopover selected={selectedCategories} onChange={onCategoriesChange} />}
                </div>
            </div>

            {/* List */}
            <div className={styles.List} onScroll={handleScroll}>
                {loading && <div className={styles.EmptyState}>Loading notifications...</div>}
                {!loading && notifications.length === 0 && <div className={styles.EmptyState}>No notifications</div>}
                {!loading &&
                    notifications.map((n) => (
                        <NotificationItem
                            key={n.id}
                            notification={n}
                            onItemAction={onItemAction}
                            onMarkRead={onMarkRead}
                            onMarkUnread={onMarkUnread}
                        />
                    ))}
                {!loading && loadingMore && <div className={styles.LoadMoreSentinel}>Loading more…</div>}
                {!loading && !hasMore && notifications.length > 0 && <div className={styles.EndOfList}>You're all caught up</div>}
            </div>
        </div>
    );
};

export default NotificationPanel;
